import React, { useMemo, useState, useEffect } from "react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  BarChart,
  Bar,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  Label,
  LabelList,
} from "recharts";
import Header from "../components/Header";
import Footer from "../components/Footer";

// ---- Helpers ----
const pct = (v) => (v == null ? null : Number(String(v).replace("%", "")));

const useBreakpoints = () => {
  const get = () => {
    const w = typeof window !== "undefined" ? window.innerWidth : 1024;
    return { width: w, isSM: w < 640, isMD: w < 768, isLG: w < 1024 };
  };
  const [bp, setBp] = useState(get());
  useEffect(() => {
    const onResize = () => setBp(get());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return bp;
};

// ---- Data: ASD vs DD (Utah vs US, 1980s–2025) ----
const utVsUsData = [
  { year: "1984–88", utahASD: 0.04, usASD: 0.05, utahDD: 1.5, usDD: 1.5 },
  { year: "1990", utahASD: 0.06, usASD: 0.07, utahDD: 1.7, usDD: 1.7 },
  { year: "2000", utahASD: 0.2, usASD: 0.07, utahDD: 2.0, usDD: 1.35 },
  { year: "2006", utahASD: 0.72, usASD: 0.9, utahDD: 3.0, usDD: 1.75 },
  { year: "2010", utahASD: 1.5, usASD: 1.5, utahDD: 4.0, usDD: 2.5 },
  { year: "2014", utahASD: 2.0, usASD: 1.7, utahDD: 4.8, usDD: 2.75 },
  { year: "2016", utahASD: 2.2, usASD: 2.24, utahDD: 5.0, usDD: 3.5 },
  { year: "2018", utahASD: 2.2, usASD: 2.8, utahDD: 5.2, usDD: 3.75 },
  { year: "2020", utahASD: 2.5, usASD: 2.76, utahDD: 5.4, usDD: 3.75 },
  { year: "2022", utahASD: 2.7, usASD: 2.8, utahDD: 5.6, usDD: 4.0 },
  { year: "2025 (proj)", utahASD: 2.7, usASD: 3.2, utahDD: 5.8, usDD: 4.5 },
];

// ---- Data: Utah Prevalence detail (ASD %, per 1k, DD %) ----
const utahDetail = [
  { year: "1984–88", asdPct: 0.04, per1k: 0.4, ddPct: 1.5 },
  { year: "1990", asdPct: 0.06, per1k: 0.6, ddPct: 1.7 },
  { year: "2000", asdPct: 0.2, per1k: 2.0, ddPct: 2.0 },
  { year: "2002", asdPct: 0.3, per1k: 3.0, ddPct: 2.5 },
  { year: "2004", asdPct: 0.55, per1k: 5.5, ddPct: 2.8 },
  { year: "2006", asdPct: 0.72, per1k: 7.2, ddPct: 3.0 },
  { year: "2008", asdPct: 1.1, per1k: 11, ddPct: 3.5 },
  { year: "2010", asdPct: 1.5, per1k: 15, ddPct: 4.0 },
  { year: "2012", asdPct: 2.2, per1k: 22, ddPct: 4.5 },
  { year: "2014", asdPct: 2.0, per1k: 20, ddPct: 4.8 },
  { year: "2016", asdPct: 2.2, per1k: 22, ddPct: 5.0 },
  { year: "2018", asdPct: 2.2, per1k: 22, ddPct: 5.2 },
  { year: "2020", asdPct: 2.5, per1k: 25, ddPct: 5.4 },
  { year: "2022", asdPct: 2.7, per1k: 27, ddPct: 5.6 },
  { year: "2025 (proj)", asdPct: 2.7, per1k: 27, ddPct: 5.8 },
];

// ---- Shared palette ----
const COLORS = ["#6366F1", "#F97316", "#22C55E", "#EF4444"];

const Card = ({ title, subtitle, children }) => (
  <div className="bg-white/80 backdrop-blur rounded-2xl shadow p-5 md:p-6 border border-slate-200">
    <div className="mb-4">
      <h2 className="text-lg md:text-xl font-semibold text-slate-800">{title}</h2>
      {subtitle && <p className="text-sm text-slate-500 mt-1">{subtitle}</p>}
    </div>
    {children}
  </div>
);

export default function Uradd1() {
  const bp = useBreakpoints();
  const chartH = bp.isSM ? 260 : 400;
  const barSize = bp.isSM ? 14 : 22;

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-slate-50 text-slate-800">
      <Header />
      <main className="mx-auto w-full max-w-7xl px-4 sm:px-6 md:px-8 mt-[8rem] space-y-8">
        <section>
          <h1 className="text-2xl md:text-3xl font-bold">
            ASD & DD Prevalence — Utah vs United States
          </h1>
          <p className="text-slate-600 mt-1">
            Trends from 1980s–2025: Utah vs national, plus Utah-specific prevalence details.
          </p>
        </section>

        {/* Chart 1: ASD Utah vs US */}
        <Card title="ASD Prevalence: Utah vs U.S." subtitle="Percent of children (1984–2025)">
          <ResponsiveContainer width="100%" height={chartH}>
            <LineChart data={utVsUsData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="year" />
              <YAxis unit="%" />
              <Tooltip formatter={(v) => `${v}%`} />
              <Legend />
              <Line dataKey="utahASD" name="Utah ASD" stroke={COLORS[0]} strokeWidth={3} />
              <Line dataKey="usASD" name="U.S. ASD" stroke={COLORS[1]} strokeWidth={3} />
            </LineChart>
          </ResponsiveContainer>
        </Card>

        {/* Chart 2: DD Utah vs US */}
        <Card title="DD Prevalence: Utah vs U.S." subtitle="Percent of children (1984–2025)">
          <ResponsiveContainer width="100%" height={chartH}>
            <LineChart data={utVsUsData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="year" />
              <YAxis unit="%" />
              <Tooltip formatter={(v) => `${v}%`} />
              <Legend />
              <Line dataKey="utahDD" name="Utah DD" stroke={COLORS[2]} strokeWidth={3} />
              <Line dataKey="usDD" name="U.S. DD" stroke={COLORS[3]} strokeWidth={3} />
            </LineChart>
          </ResponsiveContainer>
        </Card>

        {/* Chart 3: Utah-only details */}
        <Card title="ASD & DD in Utah (Detail)" subtitle="Percentages and per 1,000 (1984–2025)">
          <ResponsiveContainer width="100%" height={chartH}>
            <BarChart data={utahDetail} barCategoryGap="20%">
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="year" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="asdPct" name="ASD (%)" fill={COLORS[0]} barSize={barSize}>
                <LabelList dataKey="asdPct" position="top" formatter={(v) => `${v}%`} />
              </Bar>
              <Bar dataKey="ddPct" name="DD (%)" fill={COLORS[2]} barSize={barSize}>
                <LabelList dataKey="ddPct" position="top" formatter={(v) => `${v}%`} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>

        {/* Chart 4: Utah ASD per 1,000 */}
        <Card title="ASD Prevalence in Utah (per 1,000)">
          <ResponsiveContainer width="100%" height={chartH}>
            <LineChart data={utahDetail}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="year" />
              <YAxis />
              <Tooltip formatter={(v) => `${v} per 1,000`} />
              <Legend />
              <Line dataKey="per1k" name="ASD (per 1,000)" stroke={COLORS[1]} strokeWidth={3} />
            </LineChart>
          </ResponsiveContainer>
        </Card>
      </main>
      <Footer />
    </div>
  );
}
